import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            Last updated: March 16, 2026
          </p>

          <div className="prose prose-lg dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                1. Introduction
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                At Countr, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our countdown timer application. By using Countr, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                2. Information We Collect
              </h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Account Information</h3>
                  <p>When you create an account, we collect your email address, full name, and password. Your password is encrypted and stored securely.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Countdown Data</h3>
                  <p>We collect and store the countdown information you create, including event names, dates, descriptions, emojis, colors, categories, reminder preferences, and any images or photos you upload.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Payment Information</h3>
                  <p>If you subscribe to our Pro plan, payment information is collected and processed by Stripe, our third-party payment processor. We do not store your full credit card information on our servers. We only retain transaction records and subscription status.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Usage Information</h3>
                  <p>We automatically collect certain information about your device and how you interact with Countr, including your IP address, browser type, operating system, referring URLs, pages viewed, and access times.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                3. How We Use Your Information
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>To provide, maintain, and improve our service</li>
                <li>To create and manage your account</li>
                <li>To process your transactions and manage your subscription</li>
                <li>To send you countdown reminders via email as you've configured</li>
                <li>To respond to your comments, questions, and customer service requests</li>
                <li>To send you technical notices, updates, security alerts, and administrative messages</li>
                <li>To monitor and analyze usage patterns and trends to improve user experience</li>
                <li>To detect, prevent, and address technical issues and security vulnerabilities</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                4. Third-Party Services
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We use the following third-party services to operate Countr:
              </p>
              <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Supabase</h3>
                  <p>We use Supabase for database hosting, authentication, and file storage. Supabase is SOC 2 Type II compliant and follows industry-standard security practices. Your data is stored on secure servers with encryption at rest and in transit.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Stripe</h3>
                  <p>We use Stripe to process subscription payments. Stripe is PCI-DSS Level 1 certified, the highest level of certification in the payments industry. When you make a payment, your payment information goes directly to Stripe and is not stored on our servers.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Brevo (formerly Sendinblue)</h3>
                  <p>We use Brevo for sending email reminders and transactional emails. Brevo is GDPR compliant and does not use your email address for any purpose other than delivering the emails we send through their service.</p>
                </div>
              </div>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
                These third-party services have their own privacy policies governing the use of your information. We encourage you to review their privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                5. Cookies and Local Storage
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Countr uses cookies and local storage to enhance your experience and maintain your session. We use authentication cookies to keep you logged in and local storage to save your preferences such as dark mode settings. You can configure your browser to refuse cookies, but this may limit your ability to use certain features of our service. We do not use cookies for advertising or tracking purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                6. Data Sharing and Disclosure
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>With service providers who perform services on our behalf (Supabase, Stripe, Brevo) under strict confidentiality agreements</li>
                <li>When you choose to share a countdown via a public link, the countdown information you've chosen to share becomes publicly accessible</li>
                <li>To comply with legal obligations, such as responding to subpoenas, court orders, or other legal processes</li>
                <li>To protect the rights, property, or safety of Countr, our users, or the public</li>
                <li>In connection with a merger, acquisition, or sale of assets, in which case we will notify you before your information is transferred</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                7. Data Security
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We implement industry-standard security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. These measures include encryption of data in transit using SSL/TLS, encryption of sensitive data at rest, regular security assessments, and restricted access to personal information on a need-to-know basis. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                8. Data Retention and Deletion
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We retain your information for as long as your account is active or as needed to provide you with our services. If you wish to delete your account, you can do so from your account settings. Upon deletion, we will remove your personal information and countdown data from our active databases within 30 days. Some information may be retained in backup systems for a limited period as required for legal or operational purposes. After this retention period, all data will be permanently deleted.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                9. Your Rights and Choices
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                You have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>Access and update your account information at any time through your account settings</li>
                <li>Request a copy of the personal information we hold about you</li>
                <li>Request correction of any inaccurate or incomplete information</li>
                <li>Delete your account and associated data</li>
                <li>Opt out of email reminders by adjusting your countdown reminder settings</li>
                <li>Object to or restrict certain processing of your information</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
                To exercise any of these rights, please contact us at privacy@countr.app.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                10. Children's Privacy
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Countr is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If we discover that we have collected information from a child under 13, we will promptly delete that information. If you believe we have collected information from a child under 13, please contact us immediately at privacy@countr.app.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                11. International Data Transfers
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Your information may be transferred to and maintained on servers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. By using Countr, you consent to the transfer of your information to the United States and other countries where we operate. We take steps to ensure that your data is treated securely and in accordance with this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                12. Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically. Your continued use of Countr after any changes indicates your acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                13. Contact Us
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-4 text-gray-700 dark:text-gray-300">
                <p>Email: privacy@countr.app</p>
                <p>Support: support@countr.app</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}